import Text from '@cartwhl/ui/base/Text';

interface WinRateRow {
  label: string;
  win: number;
  tie: number;
  loss: number;
}

interface WinRateChartProps {
  model: string;
  opponent: string;
  rows: WinRateRow[];
  caption?: string;
}

// The first row is the headline result; the rest break it down by category.
export default function WinRateChart({
  model,
  opponent,
  rows,
  caption,
}: WinRateChartProps) {
  return (
    <figure className="article-breakout my-8 px-4 lg:my-12 lg:px-0">
      <div className="bg-secondary rounded-2xl p-6 lg:p-10">
        <ul className="space-y-5">
          {rows.map((row, index) => {
            const total = row.win + row.tie + row.loss;
            const winShare = Math.round((row.win / total) * 100);
            const isHeadline = index === 0;
            return (
              <li
                key={row.label}
                className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 lg:grid-cols-[200px_minmax(0,1fr)_56px] ${isHeadline ? 'border-primary border-b pb-6' : ''}`}
                aria-label={`${row.label}: ${model} preferred in ${winShare}% of comparisons`}
              >
                <Text
                  as="span"
                  variant={isHeadline ? 'web/body/medium/md' : 'web/body/md'}
                  className="text-primary"
                >
                  {row.label}
                </Text>
                <Text
                  as="span"
                  variant={isHeadline ? 'web/display/lg' : 'web/body/medium/md'}
                  className="text-primary text-right tabular-nums lg:order-last"
                >
                  {winShare}%
                </Text>
                <div
                  className={`col-span-2 flex overflow-hidden rounded-full lg:col-span-1 ${isHeadline ? 'h-4' : 'h-2.5'}`}
                  aria-hidden="true"
                >
                  <span
                    className="bg-green-50"
                    style={{ width: `${(row.win / total) * 100}%` }}
                  />
                  <span
                    className="bg-gray-45"
                    style={{ width: `${(row.tie / total) * 100}%` }}
                  />
                  <span
                    className="bg-tertiary"
                    style={{ width: `${(row.loss / total) * 100}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2" aria-hidden="true">
          {[
            ['bg-green-50', `${model} preferred`],
            ['bg-gray-45', 'No preference'],
            ['bg-tertiary', `${opponent} preferred`],
          ].map(([color, label]) => (
            <span key={label} className="flex items-center gap-2">
              <span className={`size-2.5 rounded-full ${color}`} />
              <Text
                as="span"
                variant="web/body/medium/xs"
                className="text-secondary"
              >
                {label}
              </Text>
            </span>
          ))}
        </div>
      </div>
      {caption && (
        <figcaption className="article-caption mx-auto mt-3 px-4">
          <Text
            as="span"
            variant="web/body/medium/xs"
            className="text-tertiary"
          >
            {caption}
          </Text>
        </figcaption>
      )}
    </figure>
  );
}
